import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { EnvelopeSimple, ArrowRight, ArrowClockwise, ShieldCheck } from "@phosphor-icons/react";

/**
 * Email OTP verification — reached from /login when the server responds
 * `email_otp_challenge=true`. Owner and Organization Admin only.
 *
 *  - 6-digit code emailed to the account owner
 *  - 5-minute expiry (`expires_in`)
 *  - Resend button enabled after `resend_available_in` seconds
 *  - Max 5 attempts (surfaced in-line as they burn down)
 *  - Optional "trust this device for 30 days"
 */
export default function MfaVerify() {
  const nav = useNavigate();
  const loc = useLocation();
  const { otpVerify, otpResend } = useAuth();
  const state = loc.state || {};
  const [code, setCode] = useState("");
  const [err, setErr] = useState("");
  const [info, setInfo] = useState("");
  const [busy, setBusy] = useState(false);
  const [trust, setTrust] = useState(false);
  const [attempts, setAttempts] = useState(state.attempts_remaining ?? 5);
  const [resendIn, setResendIn] = useState(state.resend_available_in ?? 30);
  const [expiresIn, setExpiresIn] = useState(state.expires_in ?? 300);
  const [challengeToken, setChallengeToken] = useState(state.challenge_token || "");
  // Dev-preview OTP inline banner is preview-only. Even if the backend
  // accidentally leaks the code in a response (defence-in-depth), we still
  // refuse to render it in production. Also gated on hostname so a stray
  // REACT_APP_EMERGENT_PREVIEW=1 in a production build cannot expose it.
  const IS_PREVIEW = (() => {
    if (process.env.REACT_APP_EMERGENT_PREVIEW !== "1") return false;
    try {
      const host = (typeof window !== "undefined" && window.location && window.location.hostname) || "";
      return host === "localhost" || host === "127.0.0.1" || host.endsWith(".preview.emergentagent.com");
    } catch (_) {
      return false;
    }
  })();
  const [devPreview, setDevPreview] = useState(
    IS_PREVIEW ? (state.otp_dev_preview || "") : ""
  );
  const timerRef = useRef(null);

  useEffect(() => {
    if (!state.challenge_token) { nav("/login", { replace: true }); return; }
    // Tick down every second. `expiresIn` and `resendIn` share the same
    // interval so the button re-enables at the right moment.
    timerRef.current = setInterval(() => {
      setResendIn((r) => (r > 0 ? r - 1 : 0));
      setExpiresIn((e) => (e > 0 ? e - 1 : 0));
    }, 1000);
    return () => clearInterval(timerRef.current);
  // Intentional mount-only effect — starts a single tick-down interval and
  // does a one-time redirect if no challenge token is present.
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setInfo(""); setBusy(true);
    const r = await otpVerify(challengeToken, code, { trust_device: trust });
    setBusy(false);
    if (!r.ok) {
      setErr(r.error);
      // Decrement local attempts counter from the error text if present.
      const m = /(\d+)\s+attempt/i.exec(r.error || "");
      if (m) setAttempts(parseInt(m[1], 10));
      return;
    }
    try {
      const { data: me } = await api.get("/auth/me");
      nav(me?.role === "owner" ? "/owner" : (state.next || "/app"));
    } catch {
      nav(state.next || "/app");
    }
  };

  const resend = async () => {
    if (resendIn > 0 || busy) return;
    setErr(""); setInfo(""); setBusy(true);
    const r = await otpResend(challengeToken);
    setBusy(false);
    if (!r.ok) { setErr(r.error); return; }
    setResendIn(r.resend_available_in || 30);
    setExpiresIn(r.expires_in || 300);
    setAttempts(r.attempts_remaining ?? 5);
    if (r.otp_dev_preview) {
      // Second layer: only surface the code if we're in the preview environment.
      if (IS_PREVIEW) setDevPreview(r.otp_dev_preview);
    }
    setInfo("A new code has been emailed to you.");
  };

  const fmt = (s) => {
    if (s <= 0) return "0:00";
    const m = Math.floor(s / 60), r = s % 60;
    return `${m}:${r.toString().padStart(2, "0")}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--bg-surface,#fafaf7)] p-6" data-testid="mfa-verify-page">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-lg p-8">
        <Logo size={40} />
        <div className="font-mono text-[11px] tracking-widest text-[var(--accent)] mt-6" data-testid="otp-role-hint">
          / SECURITY · EMAIL VERIFICATION · {String(state.role || "ADMIN").toUpperCase()}
        </div>
        <h1 className="font-display font-black text-[32px] leading-[1.05] mt-2">Check your email</h1>
        <p className="text-[13px] text-[var(--text-secondary)] mt-2">
          We just emailed a 6-digit code to <b data-testid="otp-email-masked">{state.email_masked || "your inbox"}</b>.
          It expires in <span data-testid="otp-expires-in">{fmt(expiresIn)}</span>.
        </p>

        {devPreview && (
          <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-3" data-testid="otp-dev-preview-banner">
            <div className="font-mono text-[10px] tracking-widest text-amber-800">/ DEV-PREVIEW · EMAIL PROVIDER NOT CONFIGURED</div>
            <div className="text-[12px] text-amber-900 mt-1">
              No <code>RESEND_API_KEY</code> is set on this deployment, so the code was not
              actually emailed. Use the code below to sign in; this banner will disappear the
              moment you wire a real email provider.
            </div>
            <button
              type="button"
              onClick={() => { setCode(devPreview); }}
              className="mt-2 font-mono text-2xl font-bold tracking-widest text-amber-900 bg-white rounded px-3 py-1 border border-amber-300 hover:bg-amber-100"
              data-testid="otp-dev-preview-code"
            >
              {devPreview} <span className="text-[10px] font-sans ml-1 opacity-70">click to fill</span>
            </button>
          </div>
        )}

        <form onSubmit={submit} className="mt-6 space-y-4">
          <input
            className="input font-mono text-2xl tracking-widest text-center"
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
            maxLength={6} required autoFocus
            autoComplete="one-time-code" inputMode="numeric" pattern="[0-9]*"
            data-testid="mfa-verify-code"
            placeholder="••••••"
          />

          <label className="flex items-center gap-2 text-[13px] text-[var(--text-secondary)] select-none cursor-pointer">
            <input
              type="checkbox" checked={trust}
              onChange={(e) => setTrust(e.target.checked)}
              data-testid="otp-trust-device"
              className="h-4 w-4 rounded border-slate-300"
            />
            Trust this device for 30 days (skip verification here for a while)
          </label>

          {err && <div className="text-[13px] text-red-600" data-testid="mfa-verify-error">{err}</div>}
          {info && <div className="text-[13px] text-emerald-700 flex items-center gap-1" data-testid="otp-info"><EnvelopeSimple size={14}/> {info}</div>}

          <div className="text-[12px] text-[var(--text-muted)]" data-testid="otp-attempts">
            {attempts} attempt{attempts === 1 ? "" : "s"} remaining · max 5 per code
          </div>

          <button type="submit" disabled={busy || code.length !== 6} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="mfa-verify-submit">
            {busy ? "Verifying…" : <>Verify <ShieldCheck size={16}/></>}
          </button>

          <button
            type="button"
            onClick={resend}
            disabled={resendIn > 0 || busy}
            className="w-full text-[13px] text-[var(--text-secondary)] hover:text-[var(--accent)] disabled:text-[var(--text-muted)] disabled:cursor-not-allowed flex items-center justify-center gap-1"
            data-testid="otp-resend-button"
          >
            <ArrowClockwise size={14}/>
            {resendIn > 0 ? `Resend in ${resendIn}s` : "Didn't receive it? Send again"}
          </button>

          <button type="button" onClick={() => nav("/login", { replace: true })} className="text-[12px] text-[var(--text-muted)] hover:underline w-full flex items-center justify-center gap-1" data-testid="mfa-verify-cancel">
            <ArrowRight size={12} className="rotate-180"/> Sign in as a different user
          </button>
        </form>
      </div>
    </div>
  );
}
